import { API_BASE_URL } from "./api";

export const processImage = (file, size = 100) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        canvas.width = size;
        canvas.height = size;

        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
        ctx.closePath();
        ctx.clip();

        ctx.drawImage(img, 0, 0, size, size);

        const base64 = canvas.toDataURL("image/png");
        resolve(base64);
      };

      img.onerror = reject;
      img.src = e.target.result;
    };

    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Helper: load image from dataURL or remote URL safely via backend proxy (avoid canvas taint)
const loadImageSafe = (src) => {
  return new Promise((resolve, reject) => {
    if (!src) {
      resolve(null);
      return;
    }

    if (typeof src === "string" && src.startsWith("data:")) {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (err) => {
        console.error("[imageProcessor] dataURL load error", err);
        reject(err);
      };
      img.src = src;
      return;
    }

    const proxied = `${API_BASE_URL}/api/proxy/image?url=${encodeURIComponent(
      src
    )}`;
    console.log("[imageProcessor] loading via proxy", proxied);

    // Fetch as blob, then load via object URL to keep canvas untainted
    fetch(proxied, { mode: "cors" })
      .then((res) => {
        if (!res.ok) throw new Error(`Proxy fetch failed: ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          URL.revokeObjectURL(objectUrl);
          resolve(img);
        };
        img.onerror = (err) => {
          URL.revokeObjectURL(objectUrl);
          console.error("[imageProcessor] object URL load error", err);
          reject(err);
        };
        img.src = objectUrl;
      })
      .catch((err) => {
        console.error("[imageProcessor] proxied image fetch error", err);
        reject(err);
      });
  });
};

// Улучшенная функция ресайза с лучшим качеством
const resizeImageContain = (img, boxW, boxH, circular = false) => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  canvas.width = boxW;
  canvas.height = boxH;

  // Улучшаем качество рендеринга
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  const scale = Math.min(boxW / img.width, boxH / img.height);
  const drawW = Math.round(img.width * scale);
  const drawH = Math.round(img.height * scale);
  const dx = Math.round((boxW - drawW) / 2);
  const dy = Math.round((boxH - drawH) / 2);

  if (circular) {
    const radius = Math.min(boxW, boxH) / 2;
    ctx.beginPath();
    ctx.arc(boxW / 2, boxH / 2, radius, 0, 2 * Math.PI);
    ctx.closePath();
    ctx.clip();
  }

  ctx.drawImage(img, dx, dy, drawW, drawH);
  return canvas;
};

export const combineImages = (avatarSrc, logoSrc) => {
  return new Promise((resolve, reject) => {
    Promise.all([loadImageSafe(avatarSrc), loadImageSafe(logoSrc)])
      .then(([avatarImg, logoImg]) => {
        const AVATAR_W = 150;
        const AVATAR_H = 150;
        const GAP = 6;

        let canvasWidth = AVATAR_W;
        let canvasHeight = AVATAR_H;
        let logoHeight = 0;

        if (logoImg) {
          const scale = AVATAR_W / logoImg.width;
          logoHeight = Math.round(logoImg.height * scale);
          canvasHeight = AVATAR_H + GAP + logoHeight;
        }

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";

        if (avatarImg) {
          const avatarCanvas = resizeImageContain(
            avatarImg,
            AVATAR_W,
            AVATAR_H,
            true
          );
          ctx.drawImage(avatarCanvas, 0, 0);
        }

        if (logoImg) {
          const logoY = AVATAR_H + GAP;
          ctx.drawImage(logoImg, 0, logoY, AVATAR_W, logoHeight);
        }

        const out = canvas.toDataURL("image/png");

        resolve({
          combinedImage: out,
          height: canvasHeight,
        });
      })
      .catch((err) => {
        console.error("[imageProcessor] combineImages error", err);
        reject(err);
      });
  });
};

export const createCombinedImage = async (avatarSrc, logoSrc) => {
  try {
    console.log("[imageProcessor] Creating combined image");

    // Создаем комбинированное изображение
    const combinedDataUrl = await combineImages(avatarSrc, logoSrc);
    console.log(
      "[imageProcessor] Combined image created, dataURL length:",
      combinedDataUrl.length
    );

    return combinedDataUrl;
  } catch (error) {
    console.error("[imageProcessor] Error in createCombinedImage:", error);
    throw error;
  }
};

export const uploadImageToS3 = async (dataUrl, filename = "signature.png") => {
  try {
    console.log("[imageProcessor] Starting image upload to S3");

    // Конвертируем DataURL в Blob
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    console.log("[imageProcessor] Blob created, size:", blob.size);

    // Создаем File из Blob
    const file = new File([blob], filename, {
      type: "image/png",
    });
    console.log(
      "[imageProcessor] File created:",
      file.name,
      file.type,
      file.size
    );

    // Загружаем на S3 через бекенд
    const formData = new FormData();
    formData.append("file", file);

    const uploadResponse = await fetch(`${API_BASE_URL}/api/upload`, {
      method: "POST",
      body: formData,
    });

    if (!uploadResponse.ok) {
      throw new Error(`Upload failed: ${uploadResponse.status}`);
    }

    const result = await uploadResponse.json();
    const uploadedUrl = result.image_url;

    console.log("[imageProcessor] Image uploaded successfully:", uploadedUrl);
    return uploadedUrl;
  } catch (error) {
    console.error("[imageProcessor] Error in uploadImageToS3:", error);
    throw error;
  }
};
